import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { debounce, isEmpty } from 'lodash';
import clsx from 'clsx';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Popover from '@material-ui/core/Popover';
import Tooltip from '@material-ui/core/Tooltip';
import BobConfirmButton from './BobConfirmButton';
import SearchInput from '../containers/Global/SearchInput';

import * as BobActions from '../actions/bobActions';
import * as DashboardActions from '../actions/dashboardActions';

import classes from './SensorSelect.module.css';

const SiteEntitySelector = ({
    equipmentTypes,
    ruleScoreKpis,
    selectedSensors,
    selectedEquipment,
    selectedEquipmentTypes,
    selectedRule,
    selectedRuleSensors,
    selectedSystems,
    setSelectedSystems,
    sensors,
    setRules,
    setSelectedEquipment,
    setSelectedEquipmentTypes,
    setSelectedRuleSensors,
    setSelectedSensors,
    setSelectedRule,
    setEntityScoringData,
    setRuleScoreKpis,
    systems,
    ...props
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [equipmentTypeSearchValue, setEquipmentTypeSearchValue] = useState('');
    const [systemSearchValue, setSystemSearchValue] = useState('');
    const [equipmentTypeMenuOpen, setEquipmentTypeMenuOpen] = useState(false);
    const [ruleMenuOpen, setRuleMenuOpen] = useState(false);
    const [activeEquipmentTypes, setActiveEquipmentTypes] = useState([]);
    const [activeSystems, setActiveSystems] = useState([]);
    const [siteSystems, setSiteSystems] = useState([]);
    const [siteEquipmentTypes, setSiteEquipmentTypes] = useState([]);
    const [selectAllSystems, setSelectAllSystems] = useState(false);
    const [selectAllEquipmentTypes, setSelectAllEquipmentTypes] = useState(false);

    useEffect(() => {
        setSiteSystems(systems);
    }, [systems]);

    useEffect(() => {
        setSiteEquipmentTypes(equipmentTypes);
    }, [equipmentTypes]);

    useEffect(() => {
        setActiveEquipmentTypes(selectedEquipmentTypes);
    }, [selectedEquipmentTypes]);

    useEffect(() => {
        setActiveSystems(selectedSystems);
    }, [selectedSystems]);

    const open = Boolean(anchorEl);
    const id = open ? 'nav-select-popover' : undefined;

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCheckbox = async (item, index) => {
        let updatedEquipmentTypes = [...activeEquipmentTypes];
        if (activeEquipmentTypes.find(type => type.id === item.id)) {
            updatedEquipmentTypes = updatedEquipmentTypes.filter(type => type.id !== item.id);

            if (updatedEquipmentTypes.length === activeEquipmentTypes.length) {
                setSelectAllEquipmentTypes(true);
            } else {
                setSelectAllEquipmentTypes(false);
            }

            setActiveEquipmentTypes(updatedEquipmentTypes);
        } else {
            let newType = [];
            newType.push(item);
            updatedEquipmentTypes = [...updatedEquipmentTypes, ...newType];

            if (updatedEquipmentTypes.length === activeEquipmentTypes.length) {
                setSelectAllEquipmentTypes(true);
            } else {
                setSelectAllEquipmentTypes(false);
            }

            setActiveEquipmentTypes(updatedEquipmentTypes);
        }
    };

    const handleSystemCheckbox = async (item, index) => {
        let updatedSystems = [...activeSystems];
        if (activeSystems.find(type => type.id === item.id)) {
            updatedSystems = updatedSystems.filter(type => type.id !== item.id);

            if (updatedSystems.length === activeSystems.length) {
                setSelectAllSystems(true);
            } else {
                setSelectAllSystems(false);
            }

            setActiveSystems(updatedSystems);
        } else {
            let newSystem = [];
            newSystem.push(item);
            updatedSystems = [...updatedSystems, ...newSystem];

            if (updatedSystems.length === activeSystems.length) {
                setSelectAllSystems(true);
            } else {
                setSelectAllSystems(false);
            }

            setActiveSystems(updatedSystems);
        }
    };

    const isChecked = item => {
        if (activeEquipmentTypes.find(type => type.id === item.id)) {
            return true;
        }
        return false;
    };

    const isSystemChecked = item => {
        if (activeSystems.find(system => system.id === item.id)) {
            return true;
        }
        return false;
    };

    const handleGo = () => {
        setEntityScoringData([]);
        setSelectedEquipment({});
        setSelectedRule({});
        setRules([]);

        setSelectedSystems(activeSystems);
        setSelectedEquipmentTypes(activeEquipmentTypes);
        setRuleScoreKpis([]);

        handleClose();
    };

    const handleClear = () => {
        setSelectedSystems([]);
        setSelectedEquipmentTypes([]);
        setActiveEquipmentTypes([]);
        setSelectedRuleSensors([]);
        setSelectedSensors([]);
        setSelectedRule({});
        setEntityScoringData([]);
    };

    const handleExpandEquipmentTypes = () => {
        setEquipmentTypeMenuOpen(!equipmentTypeMenuOpen);
    };

    const handleExpandRules = () => {
        setRuleMenuOpen(!ruleMenuOpen);
    };

    const handleEquipmentTypeSearch = searchFilter => {
        setEquipmentTypeSearchValue(searchFilter);

        const filteredEquipmentTypes = equipmentTypes.filter(type => type.name.toLowerCase().includes(searchFilter.toLowerCase()));
        setSiteEquipmentTypes(filteredEquipmentTypes);
    };

    const handleSystemSearch = searchFilter => {
        setSystemSearchValue(searchFilter);

        const filteredSystems = systems.filter(system => system.name.toLowerCase().includes(searchFilter.toLowerCase()));
        setSiteSystems(filteredSystems);
    };

    const formatLabel = () => {
        let label = 'Systems & Equipment Types - ';
        label = selectedSystems.length === 1 ? `${label} ${selectedSystems.length} System, ` : `${label} ${selectedSystems.length} Systems, `;
        label = selectedEquipmentTypes.length === 1 ? `${label} ${selectedEquipmentTypes.length} Type` : `${label} ${selectedEquipmentTypes.length} Types`;

        return label;
    };

    const handleSelectAllSystems = () => {
        if (!selectAllSystems) {
            setActiveSystems(siteSystems);
        } else {
            setActiveSystems([]);
        }

        setSelectAllSystems(!selectAllSystems);
    };

    const handleSelectAllEquipmentTypes = () => {
        if (!selectAllEquipmentTypes) {
            setActiveEquipmentTypes(siteEquipmentTypes);
        } else {
            setActiveEquipmentTypes([]);
        }

        setSelectAllEquipmentTypes(!selectAllEquipmentTypes);
    };

    return (
        <div className={classes.SensorSelect}>
            <Button
                aria-describedby={id}
                onClick={handleClick}
            >
                <Tooltip
                    classes={{ tooltip: classes.tooltip }}
                    title="Systems and Equipment Types"
                    placement="bottom"
                >
                    <div className={classes.Selector}>
                        <p>{formatLabel()}</p>
                    </div>
                </Tooltip>
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical:   'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical:   'top',
                    horizontal: 'left',
                }}
            >
                <List className={classes.SiteMenuItem}>
                    <ListItem
                        key={0}
                        onClick={() => handleExpandRules()}
                        classes={{
                            root: classes.RuleListItem
                        }}
                    >
                        <i className={clsx(`fas fa-chevron-${ruleMenuOpen ? 'down' : 'up'}`, classes.ArrowIcon)} aria-hidden="true" />
                        {
                            !isEmpty(activeSystems) ?
                                `Systems - ${activeSystems.length} Selected`
                                :
                                'Systems'
                        }
                    </ListItem>
                    {
                        ruleMenuOpen ?
                            <div className={classes.List}>
                                <SearchInput
                                    className={classes.SearchInput}
                                    placeholder={'Search'}
                                    search={debounce(handleSystemSearch, 500)}
                                    value={systemSearchValue}
                                    noRadius
                                />
                                {
                                    isEmpty(siteSystems) ?
                                        <List className={classes.RuleList}>
                                            <ListItem key="label" classes={{ root: classes.ListItem }}>
                                                No Systems Exist
                                            </ListItem>
                                        </List>
                                        :
                                        <List className={classes.RuleList}>
                                            <ListItem
                                                key={0}
                                                onClick={() => handleSelectAllSystems()}
                                                classes={{
                                                    root: classes.ListItem
                                                }}
                                            >
                                                <Checkbox
                                                    style={{
                                                        color: 'black',
                                                    }}
                                                    checked={selectAllSystems}
                                                />
                                                Select all
                                            </ListItem>
                                            {siteSystems.map((item, index) => (
                                                <ListItem
                                                    key={index}
                                                    onClick={() => handleSystemCheckbox(item, index)}
                                                    classes={{
                                                        root: classes.ListItem
                                                    }}
                                                >
                                                    <Checkbox
                                                        style={{
                                                            color: 'black',
                                                        }}
                                                        checked={isSystemChecked(item)}
                                                    />
                                                    {item.name}
                                                </ListItem>
                                            ))}
                                        </List>
                                }
                            </div>
                            :
                            null
                    }
                </List>
                <List className={classes.SiteMenuItem}>
                    <ListItem
                        key={0}
                        onClick={() => handleExpandEquipmentTypes()}
                        classes={{
                            root: classes.RuleListItem
                        }}
                    >
                        <i className={clsx(`fas fa-chevron-${equipmentTypeMenuOpen ? 'down' : 'up'}`, classes.ArrowIcon)} aria-hidden="true" />
                        {
                            activeEquipmentTypes.length ?
                                `Equipment Types - ${activeEquipmentTypes.length} Selected`
                                :
                                'Equipment Types'
                        }
                    </ListItem>
                    {
                        equipmentTypeMenuOpen ?
                            <div className={classes.List}>
                                <SearchInput
                                    className={classes.SearchInput}
                                    placeholder={'Search'}
                                    search={debounce(handleEquipmentTypeSearch, 500)}
                                    value={equipmentTypeSearchValue}
                                    noRadius
                                />
                                <List className={classes.RuleList}>
                                    <ListItem
                                        key={0}
                                        onClick={() => handleSelectAllEquipmentTypes()}
                                        classes={{
                                            root: classes.ListItem
                                        }}
                                    >
                                        <Checkbox
                                            style={{
                                                color: 'black',
                                            }}
                                            checked={selectAllEquipmentTypes}
                                        />
                                        Select all
                                    </ListItem>
                                    {siteEquipmentTypes.map((item, index) => (
                                        <ListItem
                                            key={index}
                                            onClick={() => handleCheckbox(item, index)}
                                            classes={{
                                                root: classes.ListItem
                                            }}
                                        >
                                            <Checkbox
                                                style={{
                                                    color: 'black',
                                                }}
                                                checked={isChecked(item)}
                                            />
                                            {item.name}
                                        </ListItem>
                                    ))}
                                </List>
                            </div>
                            :
                            null
                    }
                </List>
                <div className={classes.ScoreButtonContainer}>
                    <BobConfirmButton
                        onClick={handleGo}
                        label={'Go'}
                    />
                    <BobConfirmButton
                        className={classes.Buttons}
                        label={'Clear'}
                        onClick={handleClear}
                    />
                </div>
            </Popover>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeNavSite:           state.bob.activeNavSite,
        dashboardEndTimeStamp:   state.dashboard.dashboardEndTimeStamp,
        dashboardStartTimeStamp: state.dashboard.dashboardStartTimeStamp,
        desiredInterval:         state.dashboard.desiredInterval,
        selectedRule:            state.dashboard.selectedRule,
        selectedRuleSensors:     state.dashboard.selectedRuleSensors,
        selectedSensors:         state.dashboard.selectedSensors,
        activeNavEquipment:      state.bob.activeNavEquipment,
        ruleScoreKpis:           state.dashboard.ruleScoreKpis,
        ruleScoringConfig:       state.dashboard.ruleScoringConfig
    };
};

const mapDispatchToProps = {
    ...BobActions,
    ...DashboardActions
};

export default connect(mapStateToProps, mapDispatchToProps)(SiteEntitySelector);
